import {
  ErrorComponent,
  Link,
  rootRouteId,
  useMatch,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * Detect if an error is a SPA navigation / deserialization issue.
 *
 * TanStack Start's `seroval`-based serialization can fail when
 * deserializing complex server function responses during client-side
 * navigation. These errors are transient — a hard page load to the
 * same URL always works because the server SSRs the page fresh.
 *
 * Common patterns:
 *   - "Cannot read properties of undefined (reading 'xxx')" — seroval
 *     deserialization fails to reconstruct a reference
 *   - "reading 'path'" / "reading 'id'" — TanStack Router manifest
 *     mismatch between SSR'd pages
 *
 * Detection strategy:
 *   This component is registered as the router's `defaultErrorComponent`,
 *   which means it only fires for errors during route transitions (loader
 *   execution, route matching, deserialization). It does NOT catch random
 *   component render errors unless they bubble past all other boundaries.
 *
 *   Because of this, any TypeError about reading properties of
 *   undefined/null is almost certainly a navigation/deserialization issue
 *   rather than application code. We use the error message as the primary
 *   signal and check the stack trace as a secondary signal — but we don't
 *   require the stack trace to match since production builds minify away
 *   the function names and module paths we'd need to check.
 */
function isNavigationError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const msg = error.message;
  const stack = error.stack ?? "";

  // Primary signal: TypeError reading a property from undefined/null.
  // These are the hallmark of seroval deserialization failures and
  // TanStack Router manifest mismatches during SPA navigation.
  const isPropertyAccessError =
    msg.includes("Cannot read properties of undefined") ||
    msg.includes("Cannot read properties of null");

  // Secondary signal (development only): stack trace contains
  // recognizable serialization / router internals. In production builds
  // these identifiers are minified away, so we can't rely on them.
  const hasKnownStackFrame =
    stack.includes("deserialize") ||
    stack.includes("seroval") ||
    stack.includes("@tanstack/router") ||
    stack.includes("@tanstack/start") ||
    stack.includes("serverFnFetcher");

  // In development, require both signals to avoid masking real bugs.
  // In production, the message pattern alone is sufficient — this
  // boundary only fires for route-level errors, so false positives
  // from application code are extremely unlikely.
  if (isPropertyAccessError) {
    if (hasKnownStackFrame) return true;
    // Production: stack trace is minified, trust the message pattern
    if (typeof window !== "undefined" && import.meta.env.PROD) return true;
  }

  return false;
}

export const SSR_RELOAD_KEY = "__ssr_reload";
const SSR_RELOAD_TTL_MS = 10_000; // 10 seconds — ignore stale markers

/**
 * Clear the SSR reload marker. Called from the root layout once a page
 * has successfully rendered so that a subsequent navigation error on
 * the same URL within the TTL window will still trigger an auto-reload
 * instead of showing the error UI immediately.
 */
export function clearReloadMarker(): void {
  try {
    sessionStorage.removeItem(SSR_RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable (private mode, etc.)
  }
}

/**
 * Check whether we already attempted a hard reload for this URL recently.
 * Uses sessionStorage so the flag survives the full page reload that
 * `window.location.href = …` triggers (unlike useRef, which resets).
 */
function hasRecentReload(path: string): boolean {
  try {
    const raw = sessionStorage.getItem(SSR_RELOAD_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as { path: string; ts: number };
    // Same path and within TTL — we already tried
    return data.path === path && Date.now() - data.ts < SSR_RELOAD_TTL_MS;
  } catch {
    return false;
  }
}

function markReload(path: string): void {
  try {
    sessionStorage.setItem(
      SSR_RELOAD_KEY,
      JSON.stringify({ path, ts: Date.now() }),
    );
  } catch {
    // sessionStorage may be unavailable (private mode, etc.)
  }
}

/**
 * Global error boundary for route errors.
 *
 * Strategy:
 *   1. If the error looks like a SPA navigation/deserialization issue
 *      AND we haven't already tried a reload for this URL recently,
 *      render NOTHING (blank) and hard-reload the current URL. The
 *      server will SSR the page correctly. The user sees at most a
 *      brief blank flash, not an ugly error screen.
 *   2. If we already reloaded and it failed again, fall through to the
 *      standard error UI so the user isn't stuck in an infinite loop.
 *   3. For genuine errors (404, server errors, etc.), render the
 *      standard error UI with retry/back buttons.
 */
export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter();
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  });
  const currentPath = useRouterState({
    select: (s) => s.location.href,
  });
  const alreadyReloaded = hasRecentReload(currentPath);
  const shouldAutoReload = isNavigationError(error) && !alreadyReloaded;

  useEffect(() => {
    if (shouldAutoReload) {
      console.warn(
        "[DefaultCatchBoundary] SPA navigation error, falling back to SSR:",
        currentPath,
      );
      markReload(currentPath);
      window.location.href = currentPath;
    }
  }, [shouldAutoReload, currentPath]);

  // For navigation errors: render nothing while the hard reload triggers.
  // This prevents the ugly error flash — the user just sees the previous
  // page (or a brief blank) before the SSR'd page loads.
  if (shouldAutoReload) {
    return null;
  }

  // For genuine errors: show the standard error UI
  console.error("DefaultCatchBoundary Error:", error);

  return (
    <div className="flex min-h-[60vh] flex-1 flex-col items-center justify-center gap-6 p-4">
      <ErrorComponent error={error} />
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => router.invalidate()}
          className="button rounded px-4 py-2 text-sm"
        >
          Try Again
        </button>
        {isRoot ? (
          <Link to="/" className="button-ghost rounded px-4 py-2 text-sm font-semibold">
            Home
          </Link>
        ) : (
          <Link
            to="/"
            className="button-ghost rounded px-4 py-2 text-sm font-semibold"
            onClick={(e) => {
              e.preventDefault();
              window.history.back();
            }}
          >
            Go Back
          </Link>
        )}
      </div>
    </div>
  );
}
